import React, { Component } from "react";
import {
  withModulesManager,
  withHistory,
  formatMessage,
} from "@openimis/fe-core";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Typography,
  Paper,
  Box,
  Grid,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
} from "@material-ui/core";
import { ArrowBack as BackIcon, Edit as EditIcon } from "@material-ui/icons";
import { withStyles } from "@material-ui/core/styles";
import {
  fetchInsureeEnrollment,
  submitInsureeEnrollment,
  reviewInsureeEnrollment,
  approveInsureeEnrollment,
  rejectInsureeEnrollment,
  turnToDraftInsureeEnrollment,
  deleteInsureeEnrollment,
} from "../../actions";
import EnrollmentStatusBadge from "../../components/common/EnrollmentStatusBadge";
import EnrollmentActionButtons from "../../components/common/EnrollmentActionButtons";
import KeyValueSection from "../../components/common/KeyValueSection";
import {
  insureeFieldRows,
  enrollmentMetaRows,
  familyFieldRows,
  openCreatedEnrollmentEntity,
} from "../../utils/enrollmentData";

const styles = (theme) => ({
  page: { margin: theme.spacing(2) },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  titleWrap: { display: "flex", alignItems: "center", gap: theme.spacing(2), flex: 1 },
  title: { fontWeight: 700, color: theme.palette.primary.main },
  card: { marginBottom: theme.spacing(2) },
});

class InsureeEnrollmentDetail extends Component {
  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.match?.params?.enrollmentId;
    const nextId = this.props.match?.params?.enrollmentId;
    if (prevId !== nextId) this.load();
  }

  load = () => {
    const uuid = this.props.match?.params?.enrollmentId;
    if (uuid) {
      this.props.fetchInsureeEnrollment(this.props.modulesManager, uuid);
    }
  };

  handleBack = () => {
    const { history } = this.props;
    if (history) history.push("/enrollment/insuree-enrollment");
    else window.location.href = "/enrollment/insuree-enrollment";
  };

  handleEdit = () => {
    const uuid = this.props.enrollment?.uuid;
    if (!uuid) return;
    const { history } = this.props;
    if (history) history.push(`/enrollment/insuree/${uuid}/edit`);
    else window.location.href = `/enrollment/insuree/${uuid}/edit`;
  };

  handleOpenCreated = () => {
    const { modulesManager, history, enrollment } = this.props;
    openCreatedEnrollmentEntity(modulesManager, history, "insuree", enrollment);
  };

  run = (action, payload) => {
    const mm = this.props.modulesManager;
    return Promise.resolve(action(mm, payload)).then(() => this.load());
  };

  render() {
    const { classes, intl, enrollment, loading } = this.props;
    if (loading && !enrollment) {
      return (
        <Box className={classes.page} display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }
    if (!enrollment) {
      return (
        <Box className={classes.page}>
          <Typography>Enrollment not found.</Typography>
          <IconButton onClick={this.handleBack}>
            <BackIcon />
          </IconButton>
        </Box>
      );
    }

    const insureeRows = insureeFieldRows(enrollment.insureeData);
    const metaRows = enrollmentMetaRows(enrollment);
    const familyRows = enrollment.familyEnrollment
      ? familyFieldRows(enrollment.familyEnrollment.data)
      : [];

    return (
      <Box className={classes.page}>
        <Box className={classes.header}>
          <Box className={classes.titleWrap}>
            <IconButton onClick={this.handleBack}>
              <BackIcon />
            </IconButton>
            <Typography variant="h5" className={classes.title}>
              {formatMessage(intl, "enrollment", "insureeEnrollment.detail") ||
                "Insuree Enrollment"}
            </Typography>
            <EnrollmentStatusBadge status={enrollment.status} />
          </Box>
          {enrollment.status === "DRAFT" && (
            <IconButton color="primary" onClick={this.handleEdit}>
              <EditIcon />
            </IconButton>
          )}
        </Box>

        <Card className={classes.card}>
          <CardContent>
            <EnrollmentActionButtons
              intl={intl}
              enrollment={enrollment}
              onEdit={this.handleEdit}
              onOpenCreated={
                enrollment.createdInsuree?.uuid
                  ? this.handleOpenCreated
                  : undefined
              }
              openCreatedLabel="Open created insuree"
              onSubmit={(p) => this.run(this.props.submitInsureeEnrollment, p)}
              onReview={(p) => this.run(this.props.reviewInsureeEnrollment, p)}
              onApprove={(p) => this.run(this.props.approveInsureeEnrollment, p)}
              onReject={(p) => this.run(this.props.rejectInsureeEnrollment, p)}
              onTurnToDraft={(p) =>
                this.run(this.props.turnToDraftInsureeEnrollment, p)
              }
              onDelete={(uuid) =>
                this.run(this.props.deleteInsureeEnrollment, uuid).then(
                  this.handleBack
                )
              }
            />
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Paper className={classes.card}>
              <Box p={2}>
                <KeyValueSection title="Insuree details" rows={insureeRows} />
              </Box>
            </Paper>
            {familyRows.length > 0 && (
              <Paper className={classes.card}>
                <Box p={2}>
                  <KeyValueSection title="Linked family" rows={familyRows} />
                </Box>
              </Paper>
            )}
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper className={classes.card}>
              <Box p={2}>
                <KeyValueSection title="Enrollment info" rows={metaRows} />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }
}

const mapStateToProps = (state) => ({
  enrollment: state.enrollment?.insureeEnrollmentDetail,
  loading: state.enrollment?.fetchingInsureeEnrollments,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchInsureeEnrollment,
      submitInsureeEnrollment,
      reviewInsureeEnrollment,
      approveInsureeEnrollment,
      rejectInsureeEnrollment,
      turnToDraftInsureeEnrollment,
      deleteInsureeEnrollment,
    },
    dispatch
  );

export default withModulesManager(
  withHistory(
    injectIntl(
      withStyles(styles)(
        connect(mapStateToProps, mapDispatchToProps)(InsureeEnrollmentDetail)
      )
    )
  )
);
